import { useEffect, useRef, useState } from 'react'
import { ExternalLink, X } from 'lucide-react'
import type { WireArticleFull, WireWorkflowStage, WireArticleThumbnail, WireImageArtifact } from '../../api/types'
import { PortionMark } from '../article-viewer/common/PortionMark'
import { CmtWrapper } from '../common/cmt'
import type { CmtResult } from '../common/cmt'
import { useTopics, useRegions } from '../../hooks/useTaxonomy'
import { TaxonomyPicker } from '../common/TaxonomyPicker'

// ─── Status metadata ──────────────────────────────────────────────────────────

const STATUS_META: Record<WireWorkflowStage, { label: string; dot: string }> = {
  qc_review:           { label: 'QC Review',  dot: 'bg-yellow-400' },
  layout_review:       { label: 'Layout',      dot: 'bg-blue-400' },
  editorial_approval:  { label: 'Editorial',   dot: 'bg-violet-400' },
  scheduled:           { label: 'Scheduled',   dot: 'bg-cyan-400' },
  published:           { label: 'Published',   dot: 'bg-green-500' },
  post_pub_review:     { label: 'Post-Pub',    dot: 'bg-orange-400' },
  recalled:            { label: 'Recalled',    dot: 'bg-red-500' },
  archived:            { label: 'Archived',    dot: 'bg-fg-secondary opacity-40' },
}

// ─── Field label ─────────────────────────────────────────────────────────────

function FieldLabel({ htmlFor, children }: { htmlFor: string; children: React.ReactNode }) {
  return (
    <label
      htmlFor={htmlFor}
      className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-fg-secondary"
    >
      {children}
    </label>
  )
}

// ─── Read-only row ────────────────────────────────────────────────────────────

function ReadOnly({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-fg-secondary">{label}</p>
      <p className="text-sm text-fg">{value}</p>
    </div>
  )
}

// ─── Artifact picker ─────────────────────────────────────────────────────────

function ArtifactPicker({
  artifacts,
  onSelect,
  onClose,
}: {
  artifacts: WireImageArtifact[]
  onSelect: (a: WireImageArtifact) => void
  onClose: () => void
}) {
  return (
    <div className="rounded border border-line bg-canvas">
      <div className="flex items-center justify-between border-b border-line px-2 py-1.5">
        <p className="text-[9px] font-bold uppercase tracking-widest text-fg-secondary">
          Select from artifacts
        </p>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-0.5 text-fg-secondary hover:bg-fill hover:text-fg"
          aria-label="Close picker"
        >
          <X size={12} />
        </button>
      </div>
      {artifacts.length === 0 ? (
        <p className="px-2 py-3 text-[11px] text-fg-secondary">No image artifacts uploaded yet.</p>
      ) : (
        <div className="grid grid-cols-3 gap-1.5 p-2">
          {artifacts.map(a => (
            <button
              key={a.id}
              type="button"
              onClick={() => onSelect(a)}
              className="overflow-hidden rounded border border-line bg-fill transition-colors hover:border-accent-primary-solid"
              title={a.filename}
            >
              <div className="aspect-square overflow-hidden">
                <img
                  src={a.presignedUrl}
                  alt={a.altText ?? ''}
                  className="h-full w-full object-cover"
                />
              </div>
              <p className="truncate px-1 py-0.5 text-[9px] text-fg-secondary">{a.filename}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  article: WireArticleFull
  open: boolean
  onClose: () => void
  onUpdate: (patch: Partial<WireArticleFull>) => void
  imageArtifacts: WireImageArtifact[]
  readOnly?: boolean
}

// ─── Component ────────────────────────────────────────────────────────────────

export function ArticleMetadataDrawer({ article, open, onClose, onUpdate, imageArtifacts, readOnly }: Props) {
  const meta = STATUS_META[article.workflow.stage] ?? { label: article.workflow.stage, dot: 'bg-fg-secondary' }

  const { data: topicOptions = [], isLoading: topicsLoading } = useTopics()
  const { data: regionOptions = [], isLoading: regionsLoading } = useRegions()

  const [title, setTitle]                   = useState(article.title)
  const [cmtResult, setCmtResult]            = useState<CmtResult>({ classification: article.classification, disseminationControls: article.disseminationControls ?? [] })
  const [topics, setTopics]                 = useState<string[]>(article.topics ?? [])
  const [regions, setRegions]               = useState<string[]>(article.regions ?? [])
  const [sensitive, setSensitive]           = useState(article.sensitive ?? false)
  const [description, setDescription]       = useState(article.description ?? '')
  const [byline, setByline]                 = useState(article.byline ?? '')
  const [thumbnail, setThumbnail]           = useState<WireArticleThumbnail | null>(article.thumbnail ?? null)
  const [thumbPickerOpen, setThumbPickerOpen] = useState(false)
  const descriptionRef = useRef<HTMLTextAreaElement>(null)

  // Auto-size textareas whenever the drawer opens or their content changes
  useEffect(() => {
    if (!open || !descriptionRef.current) return
    descriptionRef.current.style.height = 'auto'
    descriptionRef.current.style.height = `${descriptionRef.current.scrollHeight}px`
  }, [open, description])

  // Sync form with article whenever the article changes (successful save, nav, etc.)
  useEffect(() => {
    setTitle(article.title)
    setCmtResult({ classification: article.classification, disseminationControls: article.disseminationControls ?? [] })
    setTopics(article.topics ?? [])
    setRegions(article.regions ?? [])
    setSensitive(article.sensitive ?? false)
    setDescription(article.description ?? '')
    setByline(article.byline ?? '')
    setThumbnail(article.thumbnail ?? null)
  }, [article.id, article.updatedAt])

  const isDirty =
    title !== article.title ||
    cmtResult.classification !== article.classification ||
    (cmtResult.disseminationControls ?? []).join(',') !== (article.disseminationControls ?? []).join(',') ||
    topics.join(',') !== (article.topics ?? []).join(',') ||
    regions.join(',') !== (article.regions ?? []).join(',') ||
    sensitive !== (article.sensitive ?? false) ||
    description !== (article.description ?? '') ||
    byline !== (article.byline ?? '') ||
    (thumbnail?.artifactId ?? null) !== (article.thumbnail?.artifactId ?? null)

  // Propagate form state to parent when closing (only if something changed).
  // The parent merges this into localArticle making the article dirty for the
  // toolbar save — no separate PATCH call.
  function handleClose() {
    if (!readOnly && isDirty) {
      onUpdate({
        title: title.trim() || article.title,
        classification: cmtResult.classification,
        disseminationControls: cmtResult.disseminationControls.length > 0 ? cmtResult.disseminationControls : undefined,
        topics: topics.length > 0 ? topics : undefined,
        regions: regions.length > 0 ? regions : undefined,
        sensitive: sensitive || undefined,
        description: description.trim() || undefined,
        byline: byline.trim() || undefined,
        thumbnail: thumbnail ?? undefined,
      })
    }
    onClose()
  }

  function handleReset() {
    setTitle(article.title)
    setCmtResult({ classification: article.classification, disseminationControls: article.disseminationControls ?? [] })
    setTopics(article.topics ?? [])
    setRegions(article.regions ?? [])
    setSensitive(article.sensitive ?? false)
    setDescription(article.description ?? '')
    setByline(article.byline ?? '')
    setThumbnail(article.thumbnail ?? null)
  }

  function selectThumbnailArtifact(a: WireImageArtifact) {
    setThumbnail({
      artifactId: a.id,
      altText: a.altText,
      caption: a.caption,
      captionClassification: a.captionClassification ?? undefined,
      captionDisseminationControls: a.captionDisseminationControls,
      classification: a.classification,
      disseminationControls: a.disseminationControls,
      credit: a.credit,
      focalPoint: a.focalPoint,
      presignedUrl: a.presignedUrl,
      s3Key: a.s3Key,
    })
    setThumbPickerOpen(false)
  }

  if (!open) return null

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-30 bg-black/30"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Drawer panel */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Article metadata"
        className="fixed right-0 top-[3rem] z-40 flex h-[calc(100vh-3rem)] w-[480px] flex-col bg-surface shadow-2xl"
      >

        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-line px-6 py-4">
          <div>
            <p className="text-[9px] font-bold uppercase tracking-widest text-fg-secondary">
              Article Metadata
            </p>
            <p className="mt-0.5 truncate text-sm font-semibold text-fg" title={article.title}>
              {article.title}
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="ml-4 shrink-0 rounded p-1 text-fg-secondary transition-colors hover:bg-fill hover:text-fg"
            aria-label="Close metadata panel"
          >
            <X size={16} />
          </button>
        </div>

        {/* Scrollable form body */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          <div className="space-y-6">

            {/* Status + Document ID — read-only */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-fg-secondary">Status</p>
                <span className="inline-flex items-center gap-1.5 text-sm text-fg">
                  <span className={`inline-block h-2 w-2 rounded-full ${meta.dot}`} aria-hidden="true" />
                  {meta.label}
                </span>
              </div>
              {article.documentId && (
                <div>
                  <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-fg-secondary">Document ID</p>
                  <p className="font-mono text-sm text-fg">{article.documentId}</p>
                </div>
              )}
            </div>

            <div className="border-t border-line" />

            {/* ── Thumbnail ─────────────────────────────────────────────── */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-[10px] font-bold uppercase tracking-widest text-fg-secondary">Thumbnail</p>
                {!readOnly && (
                <div className="flex gap-1.5">
                  <button
                    type="button"
                    onClick={() => setThumbPickerOpen(o => !o)}
                    className="rounded border border-line bg-canvas px-2 py-0.5 text-[10px] font-semibold text-fg-secondary transition-colors hover:bg-fill hover:text-fg"
                  >
                    {thumbnail ? 'Change' : 'Set'}
                  </button>
                  {thumbnail && (
                    <button
                      type="button"
                      onClick={() => setThumbnail(null)}
                      className="rounded border border-line bg-canvas px-2 py-0.5 text-[10px] font-semibold text-fg-secondary transition-colors hover:bg-fill hover:text-fg"
                    >
                      Remove
                    </button>
                  )}
                </div>
              )}
              </div>
              {thumbPickerOpen && (
                <ArtifactPicker
                  artifacts={imageArtifacts}
                  onSelect={selectThumbnailArtifact}
                  onClose={() => setThumbPickerOpen(false)}
                />
              )}
              {thumbnail ? (
                <figure className="m-0">
                  <img
                    src={thumbnail.presignedUrl ?? imageArtifacts.find(a => a.id === thumbnail.artifactId)?.presignedUrl ?? ''}
                    alt={thumbnail.altText ?? article.title}
                    className="w-full rounded border border-line object-cover"
                    style={{ maxHeight: '180px' }}
                  />
                  {thumbnail.caption && (
                    <figcaption className="mt-1 text-[11px] text-fg-secondary">
                      <PortionMark classification={thumbnail.captionClassification} disseminationControls={thumbnail.captionDisseminationControls} />
                      {thumbnail.caption}
                    </figcaption>
                  )}
                </figure>
              ) : (
                <div className="flex h-24 items-center justify-center rounded border border-dashed border-line bg-fill text-xs text-fg-secondary">
                  No thumbnail
                </div>
              )}
            </div>

            <div className="border-t border-line" />

            {/* ── Core fields ───────────────────────────────────────────── */}
            <div>
              <FieldLabel htmlFor="drawer-title">Title</FieldLabel>
              <input
                id="drawer-title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                readOnly={readOnly}
                className={`w-full rounded border border-line px-3 py-2 text-sm text-fg ${readOnly ? 'cursor-default bg-fill/50 opacity-70' : 'bg-fill focus:border-accent-primary-line focus:outline-none'}`}
              />
            </div>

            <div>
              <FieldLabel htmlFor="drawer-desc">Description</FieldLabel>
              <textarea
                ref={descriptionRef}
                id="drawer-desc"
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value)
                  e.target.style.height = 'auto'
                  e.target.style.height = `${e.target.scrollHeight}px`
                }}
                rows={4}
                placeholder="Brief description of the article's scope and findings…"
                readOnly={readOnly}
                className={`w-full resize-none overflow-hidden rounded border border-line px-3 py-2 text-sm text-fg ${readOnly ? 'cursor-default bg-fill/50 opacity-70' : 'bg-fill focus:border-accent-primary-line focus:outline-none'}`}
              />
            </div>

            <div>
              <FieldLabel htmlFor="drawer-byline">Byline</FieldLabel>
              <input
                id="drawer-byline"
                type="text"
                value={byline}
                onChange={(e) => setByline(e.target.value)}
                placeholder="e.g. Wire Staff, Multiple Sources"
                readOnly={readOnly}
                className={`w-full rounded border border-line px-3 py-2 text-sm text-fg ${readOnly ? 'cursor-default bg-fill/50 opacity-70' : 'bg-fill focus:border-accent-primary-line focus:outline-none'}`}
              />
              <p className="mt-1 text-[11px] text-fg-secondary">Overrides author names in display when set</p>
            </div>

            <div className="border-t border-line" />

            <p className="text-xs font-semibold uppercase tracking-wide text-fg-secondary">Article Access Control</p>

            <div>
              <FieldLabel htmlFor="drawer-class">Classification</FieldLabel>
              <CmtWrapper
                value={readOnly ? undefined : cmtResult}
                onChange={readOnly ? () => {} : setCmtResult}
                className={readOnly ? 'pointer-events-none opacity-60' : undefined}
              />
              {readOnly && (
                <p className="mt-1 font-mono text-sm text-fg">
                  ({[article.classification, ...(article.disseminationControls ?? [])].join('//')})
                </p>
              )}
            </div>

            <div className={readOnly ? 'pointer-events-none opacity-60' : undefined}>
              <TaxonomyPicker
                label="Topics"
                options={topicOptions}
                selected={topics}
                onChange={readOnly ? () => {} : setTopics}
                placeholder="No topics selected"
                loading={topicsLoading}
              />
            </div>

            <div className={readOnly ? 'pointer-events-none opacity-60' : undefined}>
              <TaxonomyPicker
                label="Regions"
                options={regionOptions}
                selected={regions}
                onChange={readOnly ? () => {} : setRegions}
                placeholder="No regions selected"
                loading={regionsLoading}
              />
            </div>

            <label className={`flex cursor-pointer items-center gap-2.5 ${readOnly ? 'pointer-events-none opacity-60' : ''}`}>
              <input
                type="checkbox"
                checked={sensitive}
                onChange={(e) => setSensitive(e.target.checked)}
                disabled={readOnly}
                className="h-3.5 w-3.5 rounded accent-accent-primary-solid"
              />
              <span className="text-xs font-semibold text-fg">Sensitive</span>
              <span className="text-[11px] text-fg-secondary">— requires elevated access to view</span>
            </label>

            <div className="border-t border-line" />

            {/* ── Read-only attribution ─────────────────────────────────── */}
            {article.producingOffice && (
              <ReadOnly label="Producing Office" value={article.producingOffice} />
            )}
            {article.authors && article.authors.length > 0 && (
              <ReadOnly label="Authors" value={article.authors.join(', ')} />
            )}
            {article.coauthors && article.coauthors.length > 0 && (
              <ReadOnly label="Co-authors" value={article.coauthors.join(', ')} />
            )}
            {article.contributors && article.contributors.length > 0 && (
              <ReadOnly label="Contributors" value={article.contributors.join(', ')} />
            )}
            {article.readTime !== undefined && (
              <ReadOnly label="Read Time" value={`~${article.readTime} min`} />
            )}

            {/* Word document link */}
            {article.wordDocumentUrl && (
              <div>
                <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-fg-secondary">Source Document</p>
                <a
                  href={article.wordDocumentUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm text-accent-primary-fg hover:underline"
                >
                  <ExternalLink size={12} aria-hidden="true" />
                  Open Word document
                </a>
              </div>
            )}

          </div>
        </div>

        {/* Footer actions */}
        <div className="flex shrink-0 items-center justify-end gap-3 border-t border-line px-6 py-4">
          {!readOnly && (
            <button
              type="button"
              onClick={handleReset}
              disabled={!isDirty}
              className="rounded border border-line px-4 py-2 text-sm font-semibold text-fg-secondary transition-colors hover:text-fg disabled:opacity-40"
            >
              Reset
            </button>
          )}
          <button
            type="button"
            onClick={handleClose}
            className="rounded bg-accent-primary-solid px-4 py-2 text-sm font-semibold text-white transition-opacity"
          >
            {readOnly ? 'Close' : 'Done'}
          </button>
        </div>

      </aside>
    </>
  )
}
